import { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import logo from '@src/stories/assets/logo.png';

import { compare, industries, products, services } from './menus';
import NavButton from './NavButton';
import NavItemIcon from './NavItemIcon';

export const MOBILE_MENU_ID = 'mobile-menu';

// Same data as the desktop flyouts (HeaderDesktopNavigationMenu), so both surfaces always agree.
// Section ids double as the first URL segment of their items, which drives the default-open section.
const SECTIONS = [
  { id: 'platform', title: 'Platform', items: products },
  { id: 'services', title: 'Services', items: services },
  { id: 'industries', title: 'Industries', items: industries },
  { id: 'compare', title: 'Compare', items: compare },
];

const COMPANY_LINKS = [
  { name: 'Team', href: '/team' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Security', href: '/security' },
  { name: 'Contact Us', href: '/contact' },
];

const listSx = { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 };

const rowSx = (theme, { active = false, primary = false } = {}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  minHeight: 48,
  px: 1.5,
  py: 0.75,
  borderRadius: 2,
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.9375rem',
  lineHeight: 1.3,
  fontWeight: active || primary ? 600 : 500,
  textAlign: 'left',
  whiteSpace: 'normal',
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  '& > span': { gap: 1.5, width: '100%' },
  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
  '&:focus-visible': {
    outline: 0,
    boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
  },
});

const isCurrent = (currentPage, href) => `/${currentPage.join('/')}` === href;

function MenuLink({ item, currentPage, onNavigate, primary = false, withIcon = false }) {
  const active = isCurrent(currentPage, item.href);

  return (
    <li>
      <NavButton
        to={item.href}
        aria-current={active ? 'page' : undefined}
        sx={(theme) => rowSx(theme, { active, primary })}
        onClick={() => onNavigate(item.href)}
      >
        {withIcon && <NavItemIcon size={36} icon={item.icon} name={item.name} />}
        <span>{item.name}</span>
      </NavButton>
    </li>
  );
}

function MenuSection({ section, expanded, reduceMotion, currentPage, onToggle, onNavigate }) {
  const headerId = `${MOBILE_MENU_ID}-${section.id}`;
  const panelId = `${headerId}-panel`;

  return (
    <>
      <Typography sx={{ m: 0 }} component="h2">
        <ButtonBase
          disableRipple
          id={headerId}
          aria-controls={panelId}
          aria-expanded={expanded}
          sx={(theme) => ({ ...rowSx(theme, { primary: true }), justifyContent: 'space-between' })}
          onClick={() => onToggle(section.id)}
        >
          {section.title}

          <ExpandMoreIcon
            sx={{
              color: 'text.secondary',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: reduceMotion ? 'none' : 'transform 200ms ease',
            }}
          />
        </ButtonBase>
      </Typography>

      <Collapse unmountOnExit in={expanded} timeout={reduceMotion ? 0 : 'auto'}>
        <Box pb={1} sx={listSx} role="list" id={panelId} component="ul" aria-labelledby={headerId}>
          {section.items.map((item) => (
            <MenuLink withIcon item={item} key={item.href} currentPage={currentPage} onNavigate={onNavigate} />
          ))}
        </Box>
      </Collapse>
    </>
  );
}

// Mounted fresh on every open (Drawer unmounts its children when closed), so the default-open section always follows the current page.
function MenuSections({ currentPage, reduceMotion, onNavigate }) {
  const [expanded, setExpanded] = useState(() => {
    const current = SECTIONS.find((section) => section.id === currentPage[0]);
    return { [current?.id ?? 'platform']: true };
  });

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return SECTIONS.map((section) => (
    <MenuSection
      key={section.id}
      section={section}
      currentPage={currentPage}
      reduceMotion={reduceMotion}
      expanded={Boolean(expanded[section.id])}
      onToggle={toggle}
      onNavigate={onNavigate}
    />
  ));
}

function MobileMenu({ open, currentPage = [], onClose, onNavigate }) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <Drawer
      open={open}
      anchor="right"
      transitionDuration={reduceMotion ? 0 : undefined}
      slotProps={{
        paper: {
          id: MOBILE_MENU_ID,
          role: 'dialog',
          'aria-modal': 'true',
          'aria-label': 'Site menu',
          sx: { width: 'min(360px, 88vw)', display: 'flex', flexDirection: 'column' },
        },
      }}
      onClose={onClose}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          minHeight: 64,
          pl: 2,
          pr: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box src={logo} component="img" alt="SecureOneLabs" sx={{ height: 36, width: 'auto' }} />

        <IconButton autoFocus aria-label="Close menu" sx={{ width: 44, height: 44 }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        component="nav"
        aria-label="Mobile"
        sx={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', px: 1.5, py: 1 }}
      >
        <Box sx={listSx} component="ul">
          <MenuLink primary currentPage={currentPage} item={{ name: 'Home', href: '/' }} onNavigate={onNavigate} />
        </Box>

        <MenuSections currentPage={currentPage} reduceMotion={reduceMotion} onNavigate={onNavigate} />
        <Divider sx={{ my: 1 }} />

        <Box sx={listSx} component="ul">
          {COMPANY_LINKS.map((item) => (
            <MenuLink primary item={item} key={item.href} currentPage={currentPage} onNavigate={onNavigate} />
          ))}
        </Box>
      </Box>

      <Box sx={{ flexShrink: 0, p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Button
          fullWidth
          disableElevation
          variant="contained"
          sx={{ minHeight: 48, borderRadius: '999px', textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' }}
          onClick={() => onNavigate('/demo')}
        >
          Schedule a Demo
        </Button>
      </Box>
    </Drawer>
  );
}

MobileMenu.displayName = 'Header.MobileMenu';
export default MobileMenu;
